/**
 * TradeForm — factory for modal forms built on TradeModal.
 *
 * Absorbs the repetitive plumbing shared by every trade modal:
 * error display, loading button state, validation, field reading,
 * form clearing, tradeFetch submission, and event wiring.
 *
 * Each modal becomes a config object + its unique domain logic.
 *
 * Usage:
 *   const form = createTradeForm({ overlay, fields, submitBtn, … });
 *    form.modal, form.showError, form.hideError, form.getValues,
 *    form.setValues, form.validate, form.submit, form.clearForm, form.resetBtn
 */
(function () {
	'use strict';

	function createTradeForm(config) {
		const overlay = config.overlay;
		if (!overlay) return null;

		const modal = new TradeModal(overlay);

		/* ── cache DOM refs ── */

		const fieldEntries = [];
		const fieldKeys = Object.keys(config.fields || {});
		for (let i = 0; i < fieldKeys.length; i++) {
			const key = fieldKeys[i];
			const cfg = config.fields[key];
			fieldEntries.push({
				key: key,
				el: document.getElementById(cfg.id),
				required: cfg.required || false,
				validate: cfg.validate || null,
				defaultValue: cfg.defaultValue !== undefined ? cfg.defaultValue : '',
			});
		}

		const submitBtnEl = config.submitBtn ? document.getElementById(config.submitBtn) : null;
		const errorContainerEl = config.errorContainer
			? document.getElementById(config.errorContainer)
			: null;
		const errorTextEl = config.errorText ? document.getElementById(config.errorText) : null;

		/* ── multi-state (opt-in) ── */

		const formStateEls = config.successState
			? overlay.querySelectorAll('[data-form-state="form"]')
			: null;
		const successStateEls = config.successState
			? overlay.querySelectorAll('[data-form-state="success"]')
			: null;
		let currentState = 'form';
		const hiddenClass = 'trade-modal__section--hidden';

		function showFormState() {
			currentState = 'form';
			for (let i = 0; i < formStateEls.length; i++)
				formStateEls[i].classList.remove(hiddenClass);
			for (let i = 0; i < successStateEls.length; i++)
				successStateEls[i].classList.add(hiddenClass);
		}

		function showSuccessState() {
			currentState = 'success';
			for (let i = 0; i < formStateEls.length; i++)
				formStateEls[i].classList.add(hiddenClass);
			for (let i = 0; i < successStateEls.length; i++)
				successStateEls[i].classList.remove(hiddenClass);
		}

		/* ── error display ── */

		function showError(message) {
			if (errorContainerEl && errorTextEl) {
				errorTextEl.textContent = message;
				errorContainerEl.classList.remove('trade-modal__section--hidden');
			}
		}

		function hideError() {
			if (errorContainerEl) {
				errorContainerEl.classList.add('trade-modal__section--hidden');
			}
		}

		/* ── field helpers ── */

		function getValues() {
			const values = {};
			for (let i = 0; i < fieldEntries.length; i++) {
				const entry = fieldEntries[i];
				if (!entry.el) {
					values[entry.key] = entry.defaultValue;
					continue;
				}
				const tagName = entry.el.tagName.toLowerCase();
				if (tagName === 'select') {
					values[entry.key] = entry.el.value;
				} else {
					values[entry.key] = entry.el.value.trim();
				}
			}
			return values;
		}

		function validate() {
			const values = getValues();
			for (let i = 0; i < fieldEntries.length; i++) {
				const entry = fieldEntries[i];
				const value = values[entry.key];
				if (entry.required && !value) {
					return entry.required; // error message string
				}
				if (entry.validate) {
					const err = entry.validate(value, values);
					if (err) return err;
				}
			}
			return null; // no errors
		}

		function setValues(values) {
			for (let i = 0; i < fieldEntries.length; i++) {
				const entry = fieldEntries[i];
				if (!entry.el || !(entry.key in values)) continue;
				entry.el.value = values[entry.key];
			}
		}

		function clearForm() {
			for (let i = 0; i < fieldEntries.length; i++) {
				const entry = fieldEntries[i];
				if (!entry.el) continue;
				const tagName = entry.el.tagName.toLowerCase();
				if (tagName === 'select') {
					entry.el.selectedIndex = 0;
				} else {
					entry.el.value = '';
				}
			}
			hideError();
		}

		/* ── button helpers ── */

		function resetBtn() {
			if (!submitBtnEl) return;
			submitBtnEl.classList.remove('Button--loading');
			submitBtnEl.disabled = false;
			if (config.resetBtnText !== undefined && config.resetBtnText !== null) {
				submitBtnEl.textContent = config.resetBtnText;
			}
		}

		function setLoading() {
			if (!submitBtnEl) return;
			submitBtnEl.classList.add('Button--loading');
			submitBtnEl.disabled = true;
			if (config.submit && config.submit.loadingText) {
				submitBtnEl.textContent = config.submit.loadingText;
			}
		}

		/* ── submit ── */

		async function submit() {
			hideError();

			const error = validate();
			if (error) {
				showError(error);
				return;
			}

			setLoading();

			const values = getValues();
			const ctrl = {
				modal: modal,
				showError: showError,
				hideError: hideError,
				getValues: getValues,
				setValues: setValues,
				clearForm: clearForm,
				resetBtn: resetBtn,
				overlay: overlay,
			};
			if (config.successState) {
				ctrl.showSuccessState = showSuccessState;
				ctrl.showFormState = showFormState;
				ctrl.currentState = currentState;
			}

			try {
				if (typeof config.submit.beforeSubmit === 'function') {
					const extra = await config.submit.beforeSubmit(values, ctrl);
					if (extra) {
						for (const k in extra) values[k] = extra[k];
					}
				}
				const payload = config.submit.buildPayload(values);
				const resp = await tradeFetch(
					config.submit.url,
					{
						method: config.submit.method || 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(payload),
					},
					{ source: config.submit.source },
				);

				const data = await resp.json();
				config.submit.onSuccess(data, ctrl);
			} catch (err) {
				if (typeof config.submit.onError === 'function') {
					config.submit.onError(err, ctrl);
				} else {
					console.error(config.submit.source + ' error', err);
					showError('Something went wrong. Please try again.');
				}
			} finally {
				resetBtn();
			}
		}

		/* ── modal hooks ── */

		modal.onOpen = function () {
			if (config.successState) showFormState();
			hideError();
			resetBtn();
			if (typeof config.onOpen === 'function') {
				const ctrl = {
					modal: modal,
					showError: showError,
					hideError: hideError,
					getValues: getValues,
					setValues: setValues,
					clearForm: clearForm,
					resetBtn: resetBtn,
					overlay: overlay,
				};
				if (config.successState) {
					ctrl.showSuccessState = showSuccessState;
					ctrl.showFormState = showFormState;
					ctrl.currentState = currentState;
				}
				config.onOpen(ctrl);
			}
			if (config.focusOnOpen) {
				let focusField = null;
				for (let i = 0; i < fieldEntries.length; i++) {
					if (fieldEntries[i].key === config.focusOnOpen) {
						focusField = fieldEntries[i].el;
						break;
					}
				}
				if (focusField) {
					setTimeout(function () {
						focusField.focus();
					}, 100);
				}
			}
		};

		modal.onClose = function () {
			if (config.clearOnClose !== false) {
				clearForm();
			}
			if (typeof config.onClose === 'function') {
				const ctrl = {
					modal: modal,
					showError: showError,
					hideError: hideError,
					getValues: getValues,
					setValues: setValues,
					clearForm: clearForm,
					resetBtn: resetBtn,
					overlay: overlay,
				};
				if (config.successState) {
					ctrl.showSuccessState = showSuccessState;
					ctrl.showFormState = showFormState;
					ctrl.currentState = currentState;
				}
				config.onClose(ctrl);
			}
		};

		/* ── event wiring ── */

		// Submit button click (delegated on overlay)
		if (config.submitBtn || config.successCloseBtn) {
			overlay.addEventListener('click', function (e) {
				if (config.submitBtn && e.target && e.target.id === config.submitBtn) {
					e.preventDefault();
					submit();
				}
				if (config.successCloseBtn && e.target && e.target.id === config.successCloseBtn) {
					e.preventDefault();
					modal.close();
				}
			});
		}

		// Extra listeners
		if (Array.isArray(config.listeners)) {
			for (let i = 0; i < config.listeners.length; i++) {
				const listener = config.listeners[i];
				overlay.addEventListener(listener.event, listener.handler);
			}
		}

		// Document-level trigger selector
		if (config.triggerSelector) {
			document.addEventListener('click', function (e) {
				if (
					e.target.matches(config.triggerSelector) ||
					e.target.closest(config.triggerSelector)
				) {
					e.preventDefault();
					modal.open();
				}
			});
		}

		return {
			modal: modal,
			showError: showError,
			hideError: hideError,
			getValues: getValues,
			setValues: setValues,
			validate: validate,
			submit: submit,
			clearForm: clearForm,
			resetBtn: resetBtn,
			overlay: overlay,
		};
	}

	window.createTradeForm = createTradeForm;
})();
